import { useEffect, useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  getAICheckerSafeSkips,
  updateAICheckerSafeSkip,
  type AICheckerSafeSkips,
} from '../../../../../../services/admin/adminService';
import { Search, Settings, X } from 'lucide-react';
import type { TFn } from '../../types';

/**
 * One managed safe-skip list (senders OR subjects). Owns its own add-input + search box so a long,
 * growing list stays usable: the count badge shows the size, the search filters the chips live, and
 * the chips scroll inside a capped-height box instead of pushing the modal off-screen.
 */
function SafeSkipList({ type, items, onAdd, onRemove, titleKey, descKey, placeholderKey, t }: {
  type: 'sender' | 'subject';
  items: string[];
  onAdd: (type: 'sender' | 'subject', value: string) => void;
  onRemove: (type: 'sender' | 'subject', value: string) => void;
  titleKey: string; descKey: string; placeholderKey: string; t: TFn;
}) {
  const [value, setValue] = useState('');
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const filtered = q ? items.filter((i) => i.toLowerCase().includes(q)) : items;
  const inputCls = cn('flex-1 min-w-0 px-3 py-2 rounded-lg border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize);

  const add = () => { onAdd(type, value); setValue(''); };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 className={cn(primaryText, semiBoldText, smallTextSize)}>{t(titleKey)}</h4>
        <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-semibold tabular-nums text-xs">
          {items.length}
        </span>
      </div>
      <p className={cn(secondaryText, 'text-xs')}>{t(descKey)}</p>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          placeholder={t(placeholderKey)}
          className={inputCls}
        />
        <button
          onClick={add}
          disabled={!value.trim()}
          className={cn('px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
        >
          {t('adminTesting.review.safeSkipAdd')}
        </button>
      </div>
      {/* Search only appears once the list is long enough to be worth filtering. */}
      {items.length > 8 && (
        <div className="relative">
          <Search className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5', secondaryText)} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('adminTesting.review.safeSkipSearchPlaceholder')}
            className={cn('w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700', primaryText, 'text-xs')}
          />
        </div>
      )}
      {items.length === 0 ? (
        <p className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.review.safeSkipNone')}</p>
      ) : filtered.length === 0 ? (
        <p className={cn(secondaryText, 'text-xs italic')}>{t('adminTesting.review.safeSkipNoMatch')}</p>
      ) : (
        <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1 rounded-lg border border-gray-100 dark:border-gray-800 p-2">
          {filtered.map((item) => (
            <span
              key={item}
              className={cn('inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 max-w-full', primaryText, 'text-xs')}
            >
              <span className="truncate" title={item}>{item}</span>
              <button
                onClick={() => onRemove(type, item)}
                className="shrink-0 hover:text-red-600 dark:hover:text-red-400"
                aria-label={t('adminTesting.review.safeSkipRemove')}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Settings panel to manage the user-defined safe-skip lists (senders + subjects) that EXTEND the
 * code-defined good-skip lists — so an operator can mark new senders/subjects as safe-to-skip without
 * a code change. Entries take effect on the next audit / re-audit of a matching email.
 */
export function AICheckerSafeSkipsPanel({ t }: { t: TFn }) {
  const [open, setOpen] = useState(false);
  const [skips, setSkips] = useState<AICheckerSafeSkips | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    getAICheckerSafeSkips()
      .then(setSkips)
      .catch((e: any) => setError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [open]);

  const mutate = async (type: 'sender' | 'subject', action: 'add' | 'remove', value: string) => {
    const v = value.trim();
    if (!v) return;
    try {
      setError(null);
      setSkips(await updateAICheckerSafeSkip(type, action, v));
    } catch (e: any) {
      setError(e.message || 'Failed to update');
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={t('adminTesting.review.safeSkipTitle')}
        className={cn('inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 whitespace-nowrap', primaryText, smallTextSize, semiBoldText)}
      >
        <Settings className="w-4 h-4 shrink-0" />
        {t('adminTesting.review.safeSkipButton')}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={() => setOpen(false)}>
          <div className={cn(cardClass, 'w-full max-w-2xl p-6 space-y-5 max-h-[85vh] overflow-y-auto')} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.review.safeSkipTitle')}</h3>
                <p className={cn(secondaryText, 'text-xs mt-1')}>{t('adminTesting.review.safeSkipDesc')}</p>
              </div>
              <button onClick={() => setOpen(false)} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
            </div>

            {error && <div className="text-xs text-red-600 dark:text-red-400">{error}</div>}
            {loading || !skips ? (
              <div className={cn(secondaryText, smallTextSize, 'text-center py-6')}>…</div>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2">
                <SafeSkipList
                  type="sender" items={skips.senders} onAdd={(ty, v) => mutate(ty, 'add', v)} onRemove={(ty, v) => mutate(ty, 'remove', v)}
                  titleKey="adminTesting.review.safeSkipSendersTitle" descKey="adminTesting.review.safeSkipSendersDesc" placeholderKey="adminTesting.review.safeSkipSenderPlaceholder" t={t}
                />
                <SafeSkipList
                  type="subject" items={skips.subjects} onAdd={(ty, v) => mutate(ty, 'add', v)} onRemove={(ty, v) => mutate(ty, 'remove', v)}
                  titleKey="adminTesting.review.safeSkipSubjectsTitle" descKey="adminTesting.review.safeSkipSubjectsDesc" placeholderKey="adminTesting.review.safeSkipSubjectPlaceholder" t={t}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
